import React, { useState, useEffect } from 'react';
import { configAPI } from '../services/api';
import './SignatureSettings.css';

const EMPTY_SIGNATURE = {
  mode: 'builder',
  fullName: '',
  title: '',
  company: '',
  phone: '',
  mobile: '',
  email: '',
  website: '',
  address: '',
  logoUrl: '',
  logoAlt: '',
  socialStyle: 'buttons',
  brandColor: '#1f6feb',
  linkedin: '',
  instagram: '',
  facebook: '',
  x: '',
  youtube: '',
  disclaimer: '',
  customHtml: ''
};

const FIELD_GROUPS = [
  {
    title: 'Kişi',
    fields: [
      { key: 'fullName', label: 'Ad Soyad', placeholder: 'Ad Soyad' },
      { key: 'title', label: 'Unvan', placeholder: 'Satış Müdürü' }
    ]
  },
  {
    title: 'İletişim',
    fields: [
      { key: 'phone', label: 'Telefon', placeholder: '+90 212 000 00 00' },
      { key: 'mobile', label: 'Mobil', placeholder: '+90 5xx xxx xx xx' },
      { key: 'email', label: 'E-posta (yanıt adresi)', placeholder: 'ad@firma.com', type: 'email' },
      { key: 'website', label: 'Web sitesi', placeholder: 'www.firma.com' },
      { key: 'address', label: 'Adres', placeholder: 'Mahalle, Cadde No, İlçe/İl', wide: true }
    ]
  },
  {
    title: 'Sosyal medya',
    fields: [
      { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/...' },
      { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
      { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
      { key: 'x', label: 'X (Twitter)', placeholder: 'https://x.com/...' },
      { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@...' }
    ]
  }
];

function SignatureSettings() {
  const [signature, setSignature] = useState(EMPTY_SIGNATURE);
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [signatures, setSignatures] = useState([]);
  // Düzenlenen imza: kayıtlı imzanın id'si ya da yeni imza için null
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState('');
  const [dirty, setDirty] = useState(false);

  const select = (item) => {
    setSelectedId(item ? item.id : null);
    setName(item ? item.name : '');
    setSignature({ ...EMPTY_SIGNATURE, ...(item ? item.data : {}) });
    setDirty(false);
    setFeedback(null);
  };

  const loadSignatures = async (keepId) => {
    try {
      const response = await configAPI.getSignatures();
      const list = response.data.data;
      setSignatures(list);
      return list.find((item) => item.id === keepId) || list.find((item) => item.isDefault) || list[0] || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    loadSignatures().then((initial) => {
      select(initial);
      if (!initial) setName('Varsayılan imza');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmDiscard = () => !dirty || window.confirm('Kaydedilmemiş değişiklikler kaybolacak. Devam edilsin mi?');

  const handleSelect = (item) => {
    if ((item ? item.id : null) === selectedId || !confirmDiscard()) return;
    select(item);
  };

  const handleNew = () => {
    if (!confirmDiscard()) return;
    select(null);
    setName(`İmza ${signatures.length + 1}`);
  };

  // Mevcut imzayı yeni bir imzanın başlangıcı olarak kullan
  const handleDuplicate = () => {
    if (!confirmDiscard()) return;
    const data = { ...signature };
    setSelectedId(null);
    setName(`${name} (kopya)`);
    setSignature(data);
    setDirty(true);
    setFeedback({ type: 'success', text: 'Kopya oluşturuldu; kaydedince yeni imza olarak eklenir.' });
  };

  const handleMakeDefault = async () => {
    try {
      const response = await configAPI.setDefaultSignature(selectedId);
      setSignatures(response.data.data);
      setFeedback({ type: 'success', text: response.data.message });
    } catch (error) {
      setFeedback({ type: 'error', text: error.response?.data?.message || 'Varsayılan yapılamadı' });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`"${name}" imzası silinsin mi? Bu imzayı kullanan kampanyalar varsayılan imzaya geçer.`)) return;
    try {
      const response = await configAPI.deleteSignature(selectedId);
      const list = response.data.data;
      setSignatures(list);
      select(list.find((item) => item.isDefault) || list[0] || null);
      setFeedback({ type: 'success', text: 'İmza silindi' });
    } catch (error) {
      setFeedback({ type: 'error', text: error.response?.data?.message || 'İmza silinemedi' });
    }
  };

  // Kaydetmeden canlı önizleme
  useEffect(() => {
    const timer = setTimeout(() => {
      configAPI.previewSignature(signature)
        .then((response) => setPreviewHtml(response.data.data.html))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [signature]);

  const update = (patch) => {
    setSignature((current) => ({ ...current, ...patch }));
    setDirty(true);
  };

  // E-posta istemcileri için logoyu hazırla: WebP'yi (Outlook göstermiyor) çevir, 300 piksel
  // genişliğe küçült (imzada en fazla 150 px, retina için iki katı yeterli). Şeffaflık yoksa
  // JPEG, varsa PNG kaydedilir; böylece logo birkaç on KB'ı geçmez.
  const prepareLogo = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Dosya okunamadı'));
    reader.onload = () => {
      const original = reader.result;
      const image = new Image();
      image.onerror = () => reject(new Error('Görsel açılamadı'));
      image.onload = () => {
        if (file.type === 'image/gif' && image.naturalWidth <= 300) return resolve(original);
        const scale = Math.min(1, 300 / image.naturalWidth);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.naturalWidth * scale);
        canvas.height = Math.round(image.naturalHeight * scale);
        const context = canvas.getContext('2d');
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        let transparent = false;
        for (let i = 3; i < pixels.length; i += 4) {
          if (pixels[i] < 250) { transparent = true; break; }
        }
        resolve(transparent ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.88));
      };
      image.src = original;
    };
    reader.readAsDataURL(file);
  });

  const handleLogo = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setFeedback(null);
    try {
      const dataUrl = await prepareLogo(file);
      if (dataUrl.length * 0.75 > 512 * 1024) {
        throw new Error('Logo küçültüldükten sonra da 512 KB\'tan büyük. Daha sade bir görsel deneyin.');
      }
      const response = await configAPI.uploadLogo(dataUrl);
      update({ logoUrl: response.data.data.url });
    } catch (error) {
      setFeedback({ type: 'error', text: error.response?.data?.message || error.message || 'Logo yüklenemedi' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const payload = { name, data: signature };
      const response = selectedId
        ? await configAPI.updateSignature(selectedId, payload)
        : await configAPI.createSignature(payload);
      const saved = response.data.data;
      await loadSignatures(saved.id);
      setSelectedId(saved.id);
      setName(saved.name);
      setDirty(false);
      setFeedback({ type: 'success', text: `"${saved.name}" kaydedildi. Kampanya oluştururken seçebilirsiniz.` });
    } catch (error) {
      setFeedback({ type: 'error', text: error.response?.data?.message || 'İmza kaydedilemedi' });
    } finally {
      setSaving(false);
    }
  };

  const current = signatures.find((item) => item.id === selectedId);

  return (
    <div className="settings-section signature-settings">
      <h2>Kurumsal İmzalar</h2>
      <p className="help-text">
        Farklı kişiler, markalar ya da kampanya türleri için birden fazla imza kaydedebilirsiniz. Kampanya
        oluştururken hangisinin kullanılacağını seçersiniz; seçilmezse varsayılan imza kullanılır.
      </p>

      <div className="signature-list">
        {signatures.map((item) => (
          <button
            type="button"
            key={item.id}
            className={`signature-chip ${item.id === selectedId ? 'active' : ''}`}
            onClick={() => handleSelect(item)}
          >
            {item.isDefault && <span className="default-star" title="Varsayılan imza">★</span>}
            {item.name}
          </button>
        ))}
        {selectedId === null && (
          <span className="signature-chip active new">{name || 'Yeni imza'}<em> · kaydedilmedi</em></span>
        )}
        <button type="button" className="signature-chip add" onClick={handleNew}>+ Yeni imza</button>
      </div>

      <div className="mode-toggle">
        <button
          type="button"
          className={signature.mode !== 'custom' ? 'active' : ''}
          onClick={() => update({ mode: 'builder' })}
        >
          Hazır şablon
        </button>
        <button
          type="button"
          className={signature.mode === 'custom' ? 'active' : ''}
          onClick={() => update({ mode: 'custom' })}
        >
          Kendi HTML imzam
        </button>
      </div>

      <div className="signature-layout">
        <form className="signature-form" onSubmit={handleSave}>
          <div className="sig-group">
            <h4>İmza adı</h4>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setDirty(true); }}
              placeholder="Ör. Satış ekibi, Kurumsal, Ayşe – İngilizce"
              maxLength={100}
              required
            />
            <small>Sadece sizin listenizde görünür, alıcılara gösterilmez.</small>
          </div>

          <div className="sig-group">
            <h4>Şirket</h4>
            <div className="sig-grid">
              <div className="form-group">
                <label>Şirket adı</label>
                <input
                  type="text"
                  value={signature.company}
                  onChange={(e) => update({ company: e.target.value })}
                  placeholder="AR Yazılım Danışmanlık"
                />
                <small>Gönderen adı olarak da kullanılır</small>
              </div>
              <div className="form-group">
                <label>Marka rengi</label>
                <div className="color-row">
                  <input
                    type="color"
                    value={signature.brandColor || '#1f6feb'}
                    onChange={(e) => update({ brandColor: e.target.value })}
                  />
                  <input
                    type="text"
                    value={signature.brandColor}
                    onChange={(e) => update({ brandColor: e.target.value })}
                    maxLength={7}
                  />
                </div>
              </div>
            </div>
          </div>

          {signature.mode === 'custom' ? (
            <div className="form-group">
              <label>İmza HTML kodu</label>
              <textarea
                rows="12"
                className="code-input"
                value={signature.customHtml}
                onChange={(e) => update({ customHtml: e.target.value })}
                placeholder="<table>...</table>"
              />
              <small>Outlook/Gmail'de bozulmaması için tablo tabanlı ve satır içi stilli HTML kullanın. Script ve form etiketleri temizlenir.</small>
            </div>
          ) : (
            <>
              <div className="sig-group">
                <h4>Logo</h4>
                <div className="logo-row">
                  {signature.logoUrl ? (
                    <img src={signature.logoUrl} alt="Logo" className="logo-thumb" />
                  ) : (
                    <div className="logo-thumb placeholder">Logo</div>
                  )}
                  <div className="logo-actions">
                    <label className="btn-upload">
                      {uploading ? 'Yükleniyor...' : 'Logo yükle'}
                      <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleLogo} hidden />
                    </label>
                    {signature.logoUrl && (
                      <button type="button" className="link-btn" onClick={() => update({ logoUrl: '' })}>Kaldır</button>
                    )}
                    <small>Kare ya da yatay. Yüklenen görsel e-postaya uygun boyuta küçültülür; WebP otomatik çevrilir.</small>
                  </div>
                </div>
                <div className="form-group logo-alt">
                  <label>Logo alt metni</label>
                  <input
                    type="text"
                    value={signature.logoAlt}
                    onChange={(e) => update({ logoAlt: e.target.value })}
                    placeholder={signature.company || 'Ör. Yönetil.io'}
                    maxLength={150}
                  />
                  <small>Alıcının e-posta programı görselleri engellediğinde logo yerine bu yazı görünür. Boşsa şirket adı kullanılır.</small>
                </div>
              </div>

              {FIELD_GROUPS.map((group) => (
                <div className="sig-group" key={group.title}>
                  <h4>{group.title}</h4>
                  <div className="sig-grid">
                    {group.fields.map((field) => (
                      <div className={`form-group ${field.wide ? 'wide' : ''}`} key={field.key}>
                        <label>{field.label}</label>
                        <input
                          type={field.type || 'text'}
                          value={signature[field.key]}
                          onChange={(e) => update({ [field.key]: e.target.value })}
                          placeholder={field.placeholder}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="sig-group">
                <h4>Sosyal medya görünümü</h4>
                <div className="mode-toggle compact">
                  <button
                    type="button"
                    className={signature.socialStyle !== 'links' ? 'active' : ''}
                    onClick={() => update({ socialStyle: 'buttons' })}
                  >
                    Buton
                  </button>
                  <button
                    type="button"
                    className={signature.socialStyle === 'links' ? 'active' : ''}
                    onClick={() => update({ socialStyle: 'links' })}
                  >
                    Düz bağlantı
                  </button>
                </div>
                <small>Buton: marka renginde kutular. Düz bağlantı: "LinkedIn · Instagram" şeklinde metin.</small>
              </div>
            </>
          )}

          <div className="sig-group">
            <h4>Yasal not (isteğe bağlı)</h4>
            <textarea
              rows="3"
              value={signature.disclaimer}
              onChange={(e) => update({ disclaimer: e.target.value })}
              placeholder="Ör. Ticaret unvanı, MERSİS no veya KVKK bilgilendirmesi. E-postanın en altında küçük yazıyla gösterilir."
            />
          </div>

          {feedback && <div className={`sig-feedback ${feedback.type}`}>{feedback.text}</div>}

          <div className="signature-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Kaydediliyor...' : selectedId ? 'Değişiklikleri kaydet' : 'İmzayı kaydet'}
            </button>
            {selectedId && (
              <>
                <button type="button" className="btn-outline" onClick={handleDuplicate}>Kopyala</button>
                {!current?.isDefault && (
                  <button type="button" className="btn-outline" onClick={handleMakeDefault}>Varsayılan yap</button>
                )}
                <button type="button" className="btn-outline danger" onClick={handleDelete}>Sil</button>
              </>
            )}
            {dirty && <span className="unsaved">Kaydedilmemiş değişiklik var</span>}
          </div>
        </form>

        <div className="signature-preview">
          <h4>Önizleme</h4>
          <iframe title="İmza önizleme" sandbox="" srcDoc={previewHtml} />
        </div>
      </div>
    </div>
  );
}

export default SignatureSettings;
